import { Archive } from "lucide-react";
import { useEffect, useState } from "react";
import { estimateTokens, KNOWLEDGE_KIND_LABELS, KNOWLEDGE_KINDS, type KnowledgeEntry, type KnowledgeKind } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { fmt } from "../../lib/format";
import { keys, useAction, useLinks } from "../../lib/queries";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/Feedback";
import { ChipsInput, Field, FormGrid, Input, Select, Switch, Textarea } from "../../ui/Form";

export type EntryDraft = Pick<KnowledgeEntry, "kind" | "title" | "body" | "tags" | "pinned" | "link"> & { id?: string };

const GUIDES: Record<KnowledgeKind, string> = {
  profile: "Siapa usaha ini, sejak kapan, apa yang membedakannya. 2–4 kalimat.",
  offering: "Satu layanan/produk per baris: nama, harga, lama pengerjaan. Tulis “mulai Rp …” bila harga bervariasi.",
  operations: "Alamat lengkap dengan patokan, jam buka per hari, hari libur, nomor kontak resmi.",
  faq: "Judul = pertanyaan seperti diketik pengunjung. Isi = jawaban singkat yang benar.",
  legal: "NIB, akta, NPWP, sertifikat halal/izin — persis seperti di dokumen.",
  policy: "Aturan yang mengikat: pembayaran, DP, pengiriman, garansi, pembatalan.",
  document: "Teks panjang (katalog, SOP). Pakai subjudul ## supaya dipotong dengan rapi.",
};

export function EntryEditor({ projectId, linkage, draft, onClose }: { projectId: string; linkage: boolean; draft: EntryDraft | null; onClose: () => void }) {
  const toast = useToast();
  const links = useLinks(projectId, linkage);
  const [value, setValue] = useState<EntryDraft | null>(draft);
  useEffect(() => {
    setValue(draft);
  }, [draft]);

  const invalidate = [keys.knowledge(projectId), keys.project(projectId)];
  const save = useAction((entry: EntryDraft) => {
    const { id, ...body } = entry;
    return id ? api.patch(`/projects/${projectId}/knowledge/${id}`, body) : api.post(`/projects/${projectId}/knowledge`, body);
  }, invalidate);
  const archive = useAction((id: string) => api.delete(`/projects/${projectId}/knowledge/${id}`), invalidate);

  if (!draft || !value) return null;
  const update = (patch: Partial<EntryDraft>) => setValue({ ...value, ...patch });
  const valid = value.title.trim().length >= 2 && value.body.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      width={720}
      title={value.id ? "Ubah butir pengetahuan" : "Butir pengetahuan baru"}
      description="Perubahan masuk ke draf. Pengunjung melihatnya setelah diterbitkan."
      footer={
        <>
          {value.id && (
            <Button
              variant="danger"
              icon={<Archive aria-hidden />}
              loading={archive.isPending}
              style={{ marginRight: "auto" }}
              onClick={async () => {
                if (!confirm("Arsipkan butir ini? Versi yang sudah terbit tidak berubah sampai terbit berikutnya.")) return;
                await archive.mutateAsync(value.id!);
                toast("Butir diarsipkan dari draf.");
                onClose();
              }}
            >
              Arsipkan
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={save.isPending}
            onClick={async () => {
              await save.mutateAsync(value);
              toast(value.id ? "Butir diperbarui di draf." : "Butir ditambahkan ke draf.");
              onClose();
            }}
          >
            Simpan ke draf
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <FormGrid>
          <Field label="Jenis" hint={GUIDES[value.kind]}>
            {(id, describedBy) => (
              <Select id={id} value={value.kind} aria-describedby={describedBy} onChange={(event) => update({ kind: event.target.value as KnowledgeKind })}>
                {KNOWLEDGE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {KNOWLEDGE_KIND_LABELS[kind]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Judul">{(id) => <Input id={id} value={value.title} maxLength={160} onChange={(event) => update({ title: event.target.value })} autoFocus />}</Field>
        </FormGrid>
        <Field label="Isi" hint={`${fmt.int(value.body.length)} karakter · ±${fmt.int(estimateTokens(value.body))} token. Hanya fakta yang benar — bot dilarang mengarang di luar ini.`}>
          {(id, describedBy) => (
            <Textarea id={id} value={value.body} rows={10} maxLength={40_000} aria-describedby={describedBy} onChange={(event) => update({ body: event.target.value })} />
          )}
        </Field>
        <Field label="Tag pencarian" hint="Kata yang biasa dipakai pengunjung untuk hal ini (mis. ongkir, dp, halal). Membantu pencarian pada pengetahuan besar.">
          {(id) => <ChipsInput id={id} value={value.tags} onChange={(tags) => update({ tags })} placeholder="ketik lalu Enter" max={12} />}
        </Field>
        {linkage && (
          <Field
            label="Rujukan terkait (Linkage)"
            hint="Tempat fakta ini ada di website, mis. bagian Tim di halaman Tentang. Saat jawaban memakai butir ini, rujukan ini yang disarankan lebih dulu. Langsung berlaku setelah diterbitkan."
          >
            {(id, describedBy) => (
              <Select id={id} value={value.link ?? ""} aria-describedby={describedBy} onChange={(event) => update({ link: event.target.value || null })}>
                <option value="">Tidak ada</option>
                {(links.data?.links ?? []).map((link) => (
                  <option key={link.id} value={link.key}>
                    {link.title} · {link.url.replace(/^https?:\/\/[^/]+/, "") || "/"}
                    {link.active ? "" : " (nonaktif)"}
                  </option>
                ))}
                {value.link && links.data && !links.data.links.some((link) => link.key === value.link) && <option value={value.link}>{value.link} (tidak ditemukan)</option>}
              </Select>
            )}
          </Field>
        )}
        <Switch
          checked={value.pinned}
          onChange={(pinned) => update({ pinned })}
          label="Sematkan"
          description="Selalu dikirim ke model meski mode pencarian aktif. Cocok untuk profil dan jam buka."
        />
      </div>
    </Dialog>
  );
}
